import React, { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Separator } from "../../components/ui/separator";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
    Building2,
    CreditCard,
    Clock,
    FileText,
    Palette,
    Save,
    Loader2,
    Eye,
    EyeOff,
} from "lucide-react";
import { InvoicePreview } from "../../components/pdf/PDFPreview";

export const BillingSettingsPage = () => {
    const { get, put } = useApi();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState(null);
    const [showPreview, setShowPreview] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await get("/settings/billing");
                setSettings(data);
            } catch (error) {
                toast.error("Erreur lors du chargement des paramètres");
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [get]);

    const updateField = (section, field, value) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await put("/settings/billing", {
                company_info: settings.company_info,
                payment_info: settings.payment_info,
                payment_terms: settings.payment_terms,
                invoice_footer: settings.invoice_footer,
                invoice_template: settings.invoice_template,
            });
            toast.success("Paramètres de facturation enregistrés");
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!settings) return null;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="billing-settings-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                        Paramètres de facturation
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Personnalisez vos factures avec les informations de votre entreprise
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setShowPreview(!showPreview)}
                        data-testid="toggle-invoice-preview"
                    >
                        {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                        {showPreview ? "Masquer l'aperçu" : "Aperçu"}
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-billing-settings">
                        {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        {saving ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                </div>
            </div>

            <div className={`grid gap-6 ${showPreview ? "grid-cols-1 xl:grid-cols-[1fr_420px]" : "grid-cols-1"}`}>
                <div>
                    <Tabs defaultValue="company" className="space-y-6">
                        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
                    <TabsTrigger value="company">
                        <Building2 className="w-4 h-4 mr-2 hidden sm:inline" />
                        Entreprise
                    </TabsTrigger>
                    <TabsTrigger value="payment">
                        <CreditCard className="w-4 h-4 mr-2 hidden sm:inline" />
                        Paiement
                    </TabsTrigger>
                    <TabsTrigger value="terms">
                        <Clock className="w-4 h-4 mr-2 hidden sm:inline" />
                        Conditions
                    </TabsTrigger>
                    <TabsTrigger value="footer">
                        <FileText className="w-4 h-4 mr-2 hidden sm:inline" />
                        Pied de page
                    </TabsTrigger>
                    <TabsTrigger value="template">
                        <Palette className="w-4 h-4 mr-2 hidden sm:inline" />
                        Modèle
                    </TabsTrigger>
                </TabsList>

                {/* Company Info Tab */}
                <TabsContent value="company">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Informations de l'entreprise</CardTitle>
                            <CardDescription>
                                Ces informations apparaîtront sur vos factures
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Nom légal</Label>
                                    <Input
                                        value={settings.company_info?.legal_name || ""}
                                        onChange={(e) => updateField("company_info", "legal_name", e.target.value)}
                                        placeholder="Raison sociale"
                                        data-testid="legal-name-input"
                                    />
                                </div>
                                <div>
                                    <Label>Nom commercial</Label>
                                    <Input
                                        value={settings.company_info?.commercial_name || ""}
                                        onChange={(e) => updateField("company_info", "commercial_name", e.target.value)}
                                        placeholder="Nom d'enseigne"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label>Adresse</Label>
                                    <Textarea
                                        value={settings.company_info?.address || ""}
                                        onChange={(e) => updateField("company_info", "address", e.target.value)}
                                        placeholder="Adresse complète"
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <Label>Téléphone</Label>
                                    <Input
                                        value={settings.company_info?.phone || ""}
                                        onChange={(e) => updateField("company_info", "phone", e.target.value)}
                                        placeholder="+33 1 23 45 67 89"
                                    />
                                </div>
                                <div>
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={settings.company_info?.email || ""}
                                        onChange={(e) => updateField("company_info", "email", e.target.value)}
                                        placeholder="contact@entreprise.com"
                                    />
                                </div>
                                <div>
                                    <Label>Site web</Label>
                                    <Input
                                        value={settings.company_info?.website || ""}
                                        onChange={(e) => updateField("company_info", "website", e.target.value)}
                                        placeholder="https://www.entreprise.com"
                                    />
                                </div>
                                <div>
                                    <Label>URL du logo</Label>
                                    <Input
                                        value={settings.company_info?.logo_url || ""}
                                        onChange={(e) => updateField("company_info", "logo_url", e.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <Label>N° d'entreprise (NEQ, NINEA, SIRET...)</Label>
                                    <Input
                                        value={settings.company_info?.business_number || ""}
                                        onChange={(e) => updateField("company_info", "business_number", e.target.value)}
                                        placeholder="123 456 789 00012"
                                    />
                                </div>
                                <div>
                                    <Label>N° fiscal (TVA, TPS/TVQ...)</Label>
                                    <Input
                                        value={settings.company_info?.tax_number || ""}
                                        onChange={(e) => updateField("company_info", "tax_number", e.target.value)}
                                        placeholder="FR12 345678901"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Payment Info Tab */}
                <TabsContent value="payment">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Informations de paiement</CardTitle>
                            <CardDescription>
                                Coordonnées bancaires et instructions de paiement
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Nom du bénéficiaire</Label>
                                    <Input
                                        value={settings.payment_info?.beneficiary_name || ""}
                                        onChange={(e) => updateField("payment_info", "beneficiary_name", e.target.value)}
                                        placeholder="Nom pour le virement"
                                    />
                                </div>
                                <div>
                                    <Label>Nom de la banque</Label>
                                    <Input
                                        value={settings.payment_info?.bank_name || ""}
                                        onChange={(e) => updateField("payment_info", "bank_name", e.target.value)}
                                        placeholder="Banque Exemple"
                                    />
                                </div>
                                <div>
                                    <Label>IBAN</Label>
                                    <Input
                                        value={settings.payment_info?.iban || ""}
                                        onChange={(e) => updateField("payment_info", "iban", e.target.value)}
                                        placeholder="FR76 1234 5678 9012 3456 7890 123"
                                    />
                                </div>
                                <div>
                                    <Label>BIC / SWIFT</Label>
                                    <Input
                                        value={settings.payment_info?.bic || ""}
                                        onChange={(e) => updateField("payment_info", "bic", e.target.value)}
                                        placeholder="BNPAFRPP"
                                    />
                                </div>
                                <div>
                                    <Label>Format de référence</Label>
                                    <Input
                                        value={settings.payment_info?.payment_reference_format || ""}
                                        onChange={(e) => updateField("payment_info", "payment_reference_format", e.target.value)}
                                        placeholder="FAC-{invoice_number}"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Utilisez {"{invoice_number}"} pour insérer le numéro de facture
                                    </p>
                                </div>
                            </div>
                            <Separator />
                            <div>
                                <Label>Instructions de paiement personnalisées</Label>
                                <Textarea
                                    value={settings.payment_info?.custom_instructions || ""}
                                    onChange={(e) => updateField("payment_info", "custom_instructions", e.target.value)}
                                    placeholder="Instructions supplémentaires pour le paiement..."
                                    rows={3}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Payment Terms Tab */}
                <TabsContent value="terms">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Conditions de paiement</CardTitle>
                            <CardDescription>
                                Délais et pénalités de retard
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Délai de paiement par défaut (jours)</Label>
                                    <Input
                                        type="number"
                                        value={settings.payment_terms?.default_due_days || 30}
                                        onChange={(e) => updateField("payment_terms", "default_due_days", parseInt(e.target.value) || 30)}
                                    />
                                </div>
                            </div>
                            <Separator />
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Activer les pénalités de retard</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Appliquer automatiquement des pénalités en cas de retard
                                        </p>
                                    </div>
                                    <Switch
                                        checked={settings.payment_terms?.late_penalty_enabled || false}
                                        onCheckedChange={(checked) => updateField("payment_terms", "late_penalty_enabled", checked)}
                                    />
                                </div>
                                {settings.payment_terms?.late_penalty_enabled && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                                        <div>
                                            <Label>Taux de pénalité (%)</Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={settings.payment_terms?.late_penalty_rate || ""}
                                                onChange={(e) => updateField("payment_terms", "late_penalty_rate", parseFloat(e.target.value) || null)}
                                                placeholder="3.0"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <Label>Texte de pénalité personnalisé</Label>
                                            <Textarea
                                                value={settings.payment_terms?.late_penalty_text || ""}
                                                onChange={(e) => updateField("payment_terms", "late_penalty_text", e.target.value)}
                                                placeholder="En cas de retard de paiement, des pénalités de..."
                                                rows={2}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Footer Tab */}
                <TabsContent value="footer">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Pied de facture</CardTitle>
                            <CardDescription>
                                Mentions légales et messages de remerciement
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <Label>Message de remerciement</Label>
                                <Textarea
                                    value={settings.invoice_footer?.thank_you_message || ""}
                                    onChange={(e) => updateField("invoice_footer", "thank_you_message", e.target.value)}
                                    placeholder="Merci pour votre confiance."
                                    rows={2}
                                />
                            </div>
                            <div>
                                <Label>Mention légale</Label>
                                <Textarea
                                    value={settings.invoice_footer?.legal_mention || ""}
                                    onChange={(e) => updateField("invoice_footer", "legal_mention", e.target.value)}
                                    placeholder="TVA non applicable, art. 293 B du CGI..."
                                    rows={2}
                                />
                            </div>
                            <div>
                                <Label>Texte personnalisé supplémentaire</Label>
                                <Textarea
                                    value={settings.invoice_footer?.custom_text || ""}
                                    onChange={(e) => updateField("invoice_footer", "custom_text", e.target.value)}
                                    placeholder="Tout autre texte à afficher en pied de facture..."
                                    rows={3}
                                />
                            </div>
                            <Separator />
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Afficher le nom de l'entreprise</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Nom de l'entreprise dans le pied de page
                                        </p>
                                    </div>
                                    <Switch
                                        checked={settings.invoice_footer?.show_orkestria_mention ?? true}
                                        onCheckedChange={(checked) => updateField("invoice_footer", "show_orkestria_mention", checked)}
                                    />
                                </div>
                                {settings.invoice_footer?.show_orkestria_mention && (
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label>Mention discrète</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Afficher la mention en plus petit
                                            </p>
                                        </div>
                                        <Switch
                                            checked={settings.invoice_footer?.orkestria_mention_discrete || false}
                                            onCheckedChange={(checked) => updateField("invoice_footer", "orkestria_mention_discrete", checked)}
                                        />
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Template Tab */}
                <TabsContent value="template">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Modèle de facture</CardTitle>
                            <CardDescription>
                                Personnalisez l'apparence de vos factures
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Couleur principale</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={settings.invoice_template?.primary_color || "#1E3A8A"}
                                            onChange={(e) => updateField("invoice_template", "primary_color", e.target.value)}
                                            className="w-16 h-10 p-1"
                                        />
                                        <Input
                                            value={settings.invoice_template?.primary_color || "#1E3A8A"}
                                            onChange={(e) => updateField("invoice_template", "primary_color", e.target.value)}
                                            placeholder="#1E3A8A"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Couleur secondaire</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={settings.invoice_template?.secondary_color || "#F97316"}
                                            onChange={(e) => updateField("invoice_template", "secondary_color", e.target.value)}
                                            className="w-16 h-10 p-1"
                                        />
                                        <Input
                                            value={settings.invoice_template?.secondary_color || "#F97316"}
                                            onChange={(e) => updateField("invoice_template", "secondary_color", e.target.value)}
                                            placeholder="#F97316"
                                        />
                                    </div>
                                </div>
                            </div>
                            <Separator />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Préfixe de numérotation</Label>
                                    <Input
                                        value={settings.invoice_template?.number_prefix || "FAC"}
                                        onChange={(e) => updateField("invoice_template", "number_prefix", e.target.value)}
                                        placeholder="FAC"
                                    />
                                </div>
                                <div>
                                    <Label>Format de numérotation</Label>
                                    <Input
                                        value={settings.invoice_template?.number_format || "{prefix}-{year}-{number:04d}"}
                                        onChange={(e) => updateField("invoice_template", "number_format", e.target.value)}
                                        placeholder="{prefix}-{year}-{number:04d}"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Exemple: FAC-2026-0001
                                    </p>
                                </div>
                            </div>
                            <Separator />
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>Afficher le logo</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Inclure le logo de l'entreprise sur les factures
                                    </p>
                                </div>
                                <Switch
                                    checked={settings.invoice_template?.show_logo ?? true}
                                    onCheckedChange={(checked) => updateField("invoice_template", "show_logo", checked)}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
                </div>

                {/* Live Preview Panel */}
                {showPreview && (
                    <div className="hidden xl:block sticky top-6 self-start" data-testid="invoice-preview-panel">
                        <InvoicePreview billingSettings={settings} />
                    </div>
                )}
            </div>
        </div>
    );
};
